import { AnimatePresence, motion } from "framer-motion";
import { type ReactNode, useEffect, useState } from "react";
import { copy } from "../content/copy";
import { useTypewriter } from "../hooks/useTypewriter";

type StepId =
  | "empty"
  | "code"
  | "cluster"
  | "terminal"
  | "problem"
  | "resize"
  | "save"
  | "summary"
  | "candidate";

interface Step {
  id: StepId;
  duration: number;
}

const STEPS: Step[] = [
  { id: "empty", duration: 900 },
  { id: "code", duration: 2500 },
  { id: "cluster", duration: 2000 },
  { id: "terminal", duration: 2000 },
  { id: "problem", duration: 2000 },
  { id: "resize", duration: 1500 },
  { id: "save", duration: 2000 },
  { id: "summary", duration: 2500 },
  { id: "candidate", duration: 3000 },
];

const TOTAL_DURATION_MS = STEPS.reduce((sum, s) => sum + s.duration, 0);
const stepIndex = (id: StepId) => STEPS.findIndex((s) => s.id === id);

/* =========================================================================
   Generic helpers
   ========================================================================= */
function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    const mq = window.matchMedia(query);
    setMatches(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

function CodeLine({
  n,
  active,
  children,
}: {
  n: number;
  active?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className={`portal-demo__code-line${active ? " portal-demo__code-line--active" : ""}`}>
      <span className="portal-demo__code-ln">{n}</span>
      <span>{children}</span>
    </div>
  );
}

function PanelHeader({ name, badges }: { name: string; badges: string[] }) {
  return (
    <header className="portal-demo__panel-head">
      <span className="portal-demo__panel-title">{name}</span>
      <div className="portal-demo__panel-badges">
        {badges.map((b) => (
          <span key={b} className="portal-demo__panel-badge">
            {b}
          </span>
        ))}
      </div>
    </header>
  );
}

function LanguageBadge({
  active,
  options,
}: {
  active: string;
  options: readonly string[];
}) {
  return (
    <motion.div
      className="portal-demo__lang-badge"
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, delay: 0.1 }}
    >
      {options.map((lang) => (
        <span
          key={lang}
          className={`portal-demo__lang-pill${lang === active ? " portal-demo__lang-pill--active" : ""}`}
        >
          {lang === active ? "● " : "○ "}
          {lang}
        </span>
      ))}
    </motion.div>
  );
}

function ProblemBody({ text, typing }: { text: string; typing: boolean }) {
  const { display, done } = useTypewriter(text, { speed: 28, startDelay: 200 });
  const out = typing ? display : text;
  const showCursor = typing && !done;
  return (
    <div className="portal-demo__problem">
      <p>
        {out}
        {showCursor && <span className="portal-demo__caret" />}
      </p>
    </div>
  );
}

function CaretLine({ children }: { children?: ReactNode }) {
  return (
    <div className="portal-demo__term-line">
      <span className="prompt">$</span> {children}
      <span className="portal-demo__caret" />
    </div>
  );
}

/* =========================================================================
   Per-domain CODE EDITOR bodies
   ========================================================================= */
function K8sCodeBody() {
  return (
    <pre className="portal-demo__code">
      <CodeLine n={12}>
        <span className="kw">package</span> <span className="ns">controllers</span>
      </CodeLine>
      <CodeLine n={13} />
      <CodeLine n={14}>
        <span className="kw">type</span> <span className="ty">PodAutoScaler</span>{" "}
        <span className="kw">struct</span> {"{"}
      </CodeLine>
      <CodeLine n={15}>
        {"  "}Client <span className="ty">client.Client</span>
      </CodeLine>
      <CodeLine n={16}>
        {"  "}Scheme <span className="kw">*</span>
        <span className="ty">runtime.Scheme</span>
      </CodeLine>
      <CodeLine n={17}>{"}"}</CodeLine>
      <CodeLine n={18} />
      <CodeLine n={19} active>
        <span className="kw">func</span> (r <span className="kw">*</span>
        <span className="ty">PodAutoScaler</span>)<span className="fn"> Reconcile</span>(ctx{" "}
        <span className="ty">context.Context</span>) {"{"}
      </CodeLine>
      <CodeLine n={20}>
        {"  "}
        <span className="cm">{"// fetch the custom resource"}</span>
      </CodeLine>
      <CodeLine n={21}>
        {"  "}
        <span className="kw">var</span> scaler <span className="ty">v1.PodAutoScaler</span>
      </CodeLine>
    </pre>
  );
}

function LlvmCodeBody() {
  return (
    <pre className="portal-demo__code">
      <CodeLine n={8}>
        <span className="kw">namespace</span> <span className="ns">llvm</span> {"{"}
      </CodeLine>
      <CodeLine n={9} />
      <CodeLine n={10}>
        <span className="kw">class</span> <span className="ty">HoistInvariantsPass</span>{" "}
        <span className="kw">: public</span> <span className="ty">FunctionPass</span> {"{"}
      </CodeLine>
      <CodeLine n={11}>
        <span className="kw">public</span>:
      </CodeLine>
      <CodeLine n={12}>
        {"  "}
        <span className="kw">static char</span> ID;
      </CodeLine>
      <CodeLine n={13} />
      <CodeLine n={14} active>
        {"  "}
        <span className="kw">bool</span> <span className="fn">runOnFunction</span>(
        <span className="ty">Function</span> &F) <span className="kw">override</span> {"{"}
      </CodeLine>
      <CodeLine n={15}>
        {"    "}
        <span className="ty">LoopInfo</span> &LI =<span className="fn"> getAnalysis</span>&lt;
        <span className="ty">LoopInfoWrapperPass</span>
        &gt;().<span className="fn">getLoopInfo</span>();
      </CodeLine>
      <CodeLine n={16}>
        {"    "}
        <span className="kw">for</span> (<span className="ty">Loop</span> *L : LI)
      </CodeLine>
      <CodeLine n={17}>
        {"      "}
        <span className="fn">hoistInvariantLoads</span>(*L);
      </CodeLine>
      <CodeLine n={18}>{"  }"}</CodeLine>
      <CodeLine n={19}>{"};"}</CodeLine>
    </pre>
  );
}

function EbpfCodeBody() {
  return (
    <pre className="portal-demo__code">
      <CodeLine n={1}>
        <span className="kw">#include</span> <span className="str">&lt;linux/bpf.h&gt;</span>
      </CodeLine>
      <CodeLine n={2}>
        <span className="kw">#include</span> <span className="str">&lt;bpf/bpf_helpers.h&gt;</span>
      </CodeLine>
      <CodeLine n={3} />
      <CodeLine n={4}>
        <span className="kw">struct</span> {"{"}
      </CodeLine>
      <CodeLine n={5}>
        {"  "}
        <span className="fn">__uint</span>(type, <span className="ns">BPF_MAP_TYPE_HASH</span>);
      </CodeLine>
      <CodeLine n={6}>
        {"  "}
        <span className="fn">__type</span>(key, <span className="ty">__u32</span>);
      </CodeLine>
      <CodeLine n={7}>
        {"} whitelist "}
        <span className="fn">SEC</span>(<span className="str">".maps"</span>);
      </CodeLine>
      <CodeLine n={8} />
      <CodeLine n={9}>
        <span className="fn">SEC</span>(<span className="str">"xdp"</span>)
      </CodeLine>
      <CodeLine n={10} active>
        <span className="kw">int</span> <span className="fn">xdp_drop</span>(
        <span className="kw">struct</span> <span className="ty">xdp_md</span> *ctx) {"{"}
      </CodeLine>
      <CodeLine n={11}>
        {"  "}
        <span className="cm">{"// drop tcp:8080 unless whitelisted"}</span>
      </CodeLine>
      <CodeLine n={12}>
        {"  "}
        <span className="kw">if</span> (port == <span className="fn">bpf_htons</span>(8080))
      </CodeLine>
      <CodeLine n={13}>
        {"    "}
        <span className="kw">return</span> <span className="ns">XDP_DROP{";"}</span>
      </CodeLine>
    </pre>
  );
}

function SreCodeBody() {
  return (
    <pre className="portal-demo__code">
      <CodeLine n={1}>
        <span className="cm">{"# checkout 5xx error rate"}</span>
      </CodeLine>
      <CodeLine n={2}>
        <span className="fn">sum</span> <span className="kw">by</span> (status_code) (
      </CodeLine>
      <CodeLine n={3}>
        {"  "}
        <span className="fn">rate</span>(<span className="ns">http_requests_total</span>
        {"{"}
      </CodeLine>
      <CodeLine n={4}>
        {"    "}service=<span className="str">"checkout"</span>,
      </CodeLine>
      <CodeLine n={5}>
        {"    "}status_code=~<span className="str">"5.."</span>
      </CodeLine>
      <CodeLine n={6}>
        {"  "}
        {"}[5m])"}
      </CodeLine>
      <CodeLine n={7}>{")"}</CodeLine>
      <CodeLine n={8}>/</CodeLine>
      <CodeLine n={9} active>
        <span className="fn">sum</span> ( <span className="fn">rate</span>(
        <span className="ns">http_requests_total</span>
        {"[5m]"})) <span className="kw">{">"}</span> 0.05
      </CodeLine>
    </pre>
  );
}

/* =========================================================================
   Per-domain TOP-RIGHT panel bodies
   ========================================================================= */
function ClusterBody({ candidate }: { candidate?: boolean }) {
  return (
    <div className="portal-demo__cluster">
      <div className="portal-demo__cluster-head">
        <span>NAME</span>
        <span>NAMESPACE</span>
        <span>STATUS</span>
      </div>
      <div className="portal-demo__cluster-row">
        <span className="portal-demo__cluster-name">podautoscaler-controller-7f8d</span>
        <span className="portal-demo__cluster-ns">nichehire-system</span>
        <span className="portal-demo__cluster-status">
          <span className="portal-demo__cluster-dot" /> Running
        </span>
      </div>
      <div className="portal-demo__cluster-row">
        <span className="portal-demo__cluster-name">metrics-server-58c47b6d9</span>
        <span className="portal-demo__cluster-ns">kube-system</span>
        <span className="portal-demo__cluster-status">
          <span className="portal-demo__cluster-dot" /> Running
        </span>
      </div>
      <div className="portal-demo__cluster-row">
        <span className="portal-demo__cluster-name">coredns-6d4b75cb6d</span>
        <span className="portal-demo__cluster-ns">kube-system</span>
        <span className="portal-demo__cluster-status">
          <span className="portal-demo__cluster-dot" /> Running
        </span>
      </div>
      {candidate && (
        <motion.div
          className="portal-demo__cluster-row portal-demo__cluster-row--new"
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.2, duration: 0.4 }}
        >
          <span className="portal-demo__cluster-name">candidate-deploy-x9k2</span>
          <span className="portal-demo__cluster-ns">default</span>
          <span className="portal-demo__cluster-status">
            <span className="portal-demo__cluster-dot" /> Running
          </span>
        </motion.div>
      )}
    </div>
  );
}

function IrBody() {
  return (
    <pre className="portal-demo__ir">
      <span>; ModuleID = 'test.ll'</span>
      <span>
        <span className="kw">define</span> <span className="ty">i32</span>{" "}
        <span className="ns">@sum</span>(<span className="ty">i32</span> %n) {"{"}
      </span>
      <span>
        <span className="ns">entry:</span>
      </span>
      <span>
        {"  "}%sum = <span className="kw">alloca</span> <span className="ty">i32</span>
      </span>
      <span>
        {"  "}
        <span className="kw">store</span> <span className="ty">i32</span> 0,{" "}
        <span className="ty">i32</span>* %sum
      </span>
      <span>
        {"  "}
        <span className="kw">br</span> <span className="kw">label</span>{" "}
        <span className="ns">%loop</span>
      </span>
      <span>
        <span className="ns">loop:</span>
      </span>
      <span>
        {"  "}%i = <span className="kw">phi</span> <span className="ty">i32</span> [ 0,{" "}
        <span className="ns">%entry</span> ]
      </span>
      <span>
        {"  "}%load = <span className="kw">load</span> <span className="ty">i32</span>*,{" "}
        <span className="ty">i32</span>** <span className="ns">@ptr</span>{" "}
        <span className="cm">; hoistable</span>
      </span>
      <span>
        {"  "}
        <span className="kw">ret</span> <span className="ty">i32</span> %sum
      </span>
      <span>{"}"}</span>
    </pre>
  );
}

function VerifierBody() {
  return (
    <pre className="portal-demo__ir">
      <span className="cm">verifier: loading prog xdp_drop</span>
      <span>0: (b7) r2 = 0</span>
      <span>1: (61) r1 = *(u32 *)(r1 +4)</span>
      <span>2: (15) if r1 == 0x0 goto pc+8</span>
      <span>3: (61) r3 = *(u32 *)(r1 +0)</span>
      <span>4: (b7) r0 = 1</span>
      <span>5: (95) exit</span>
      <span className="ok">verifier: bounds check ok</span>
      <span className="ok">verifier: load_skip safe</span>
      <span className="ok">verifier: prog loaded · 12 insns · stack 8</span>
    </pre>
  );
}

function MetricsBody() {
  // Shape of the bars — error rate climbs from baseline to alert
  const bars = [
    { id: "t1", v: 1.2 },
    { id: "t2", v: 1.5 },
    { id: "t3", v: 1.4 },
    { id: "t4", v: 1.8 },
    { id: "t5", v: 2.1 },
    { id: "t6", v: 2.4 },
    { id: "t7", v: 8.1 },
    { id: "t8", v: 11.2 },
    { id: "t9", v: 12.4 },
    { id: "t10", v: 12.1 },
    { id: "t11", v: 11.8 },
    { id: "t12", v: 12.3 },
  ];
  return (
    <div className="portal-demo__metrics">
      <div className="portal-demo__metrics-head">
        <span>checkout · 5xx rate · 30m</span>
        <span className="portal-demo__metrics-alert">12.4%</span>
      </div>
      <div className="portal-demo__metrics-bars">
        {bars.map((b) => (
          <div
            key={b.id}
            className={`portal-demo__metrics-bar${b.v > 5 ? " portal-demo__metrics-bar--alert" : ""}`}
            style={{ height: `${Math.min(b.v * 7, 95)}%` }}
          />
        ))}
      </div>
      <div className="portal-demo__metrics-foot">
        <span>0%</span>
        <span>baseline 1.2%</span>
        <span>alert &gt; 5%</span>
      </div>
    </div>
  );
}

/* =========================================================================
   Per-domain TERMINAL bodies
   ========================================================================= */
function K8sTerminalBody() {
  return (
    <div className="portal-demo__term">
      <div className="portal-demo__term-line">
        <span className="prompt">$</span> kubectl get pods -A
      </div>
      <div className="portal-demo__term-line portal-demo__term-line--out">
        NAMESPACE NAME STATUS
      </div>
      <div className="portal-demo__term-line portal-demo__term-line--out">
        nichehire-system podautoscaler-controller-7f8d Running
      </div>
      <CaretLine />
    </div>
  );
}

function LlvmTerminalBody() {
  return (
    <div className="portal-demo__term">
      <div className="portal-demo__term-line">
        <span className="prompt">$</span> opt -S -O2 test.ll -o /dev/null
      </div>
      <div className="portal-demo__term-line portal-demo__term-line--out">
        ; ModuleID = 'test.ll'
      </div>
      <div className="portal-demo__term-line portal-demo__term-line--out">
        ; pass &lt;HoistInvariants&gt; ran in 0.4ms
      </div>
      <CaretLine />
    </div>
  );
}

function EbpfTerminalBody() {
  return (
    <div className="portal-demo__term">
      <div className="portal-demo__term-line">
        <span className="prompt">$</span> bpftool prog show
      </div>
      <div className="portal-demo__term-line portal-demo__term-line--out">
        12: xdp name xdp_drop tag e3c2 gpl
      </div>
      <div className="portal-demo__term-line portal-demo__term-line--out">
        loaded_at 14:32 uid 0 xlated 96B jited 64B
      </div>
      <CaretLine />
    </div>
  );
}

function SreTerminalBody() {
  return (
    <div className="portal-demo__term">
      <div className="portal-demo__term-line">
        <span className="prompt">$</span> kubectl logs -l app=checkout --tail=200 | grep ERROR
      </div>
      <div className="portal-demo__term-line portal-demo__term-line--out">
        14:31:08 ERROR upstream timeout connecting to db.checkout
      </div>
      <div className="portal-demo__term-line portal-demo__term-line--out">
        14:31:09 ERROR pool exhausted: 30/30 connections
      </div>
      <CaretLine />
    </div>
  );
}

/* =========================================================================
   Domain content modules
   ========================================================================= */

interface DomainContent {
  id: "k8s" | "llvm" | "ebpf" | "sre";
  label: string;
  windowTitle: string;
  editor: {
    language: string;
    languages: readonly string[];
    Body: React.ComponentType;
  };
  topPanel: {
    name: string;
    badges: string[];
    Body: React.ComponentType;
    CandidateBody?: React.ComponentType;
  };
  terminal: {
    badges: string[];
    Body: React.ComponentType;
    candidateCmd: string;
  };
  problem: string;
  template: {
    name: string;
    domain: string;
    domainSlug: string;
    components: Array<{ key: string; value: string }>;
    layoutLeft: string;
    layoutRight: string;
  };
  status: string[];
}

const K8S: DomainContent = {
  id: "k8s",
  label: "K8s",
  windowTitle: "Senior K8s Operator Interview",
  editor: {
    language: "Go",
    languages: ["Go", "C++", "Rust", "TypeScript"],
    Body: K8sCodeBody,
  },
  topPanel: {
    name: "Cluster Viewer",
    badges: ["live cluster feed"],
    Body: () => <ClusterBody />,
    CandidateBody: () => <ClusterBody candidate />,
  },
  terminal: {
    badges: ["shared terminal", "candidate controls"],
    Body: K8sTerminalBody,
    candidateCmd: "kubectl apply -f config/crd/bases/",
  },
  problem:
    "Implement a PodAutoScaler controller that watches a custom resource and scales a target Deployment within defined min/max bounds. Handle cleanup on deletion.",
  template: {
    name: "Senior K8s Operator Interview",
    domain: "Kubernetes / Platform Engineering",
    domainSlug: "kubernetes-operator",
    components: [
      { key: "code-editor", value: "go · language server · workspace sync" },
      { key: "cluster-viewer", value: "live k8s cluster feed" },
      { key: "terminal", value: "bash · candidate writes" },
      { key: "problem", value: "static content" },
    ],
    layoutLeft: "code-editor + problem",
    layoutRight: "cluster-viewer / terminal",
  },
  status: ["k3s · nichehire-system", "go 1.22"],
};

const LLVM: DomainContent = {
  id: "llvm",
  label: "LLVM",
  windowTitle: "Senior Compiler Engineer Interview",
  editor: {
    language: "C++",
    languages: ["C++", "Rust", "Go", "TypeScript"],
    Body: LlvmCodeBody,
  },
  topPanel: {
    name: "IR Viewer",
    badges: ["live IR feed"],
    Body: IrBody,
  },
  terminal: {
    badges: ["shared terminal", "candidate controls"],
    Body: LlvmTerminalBody,
    candidateCmd: "clang++ -O2 -Xclang -load -Xclang ./HoistInvariants.so test.cpp",
  },
  problem:
    "Implement an LLVM function pass that hoists loop-invariant memory loads above their containing loop. Verify with the provided IR fixtures.",
  template: {
    name: "Senior Compiler Engineer Interview",
    domain: "LLVM / Compiler Engineering",
    domainSlug: "llvm-compiler",
    components: [
      { key: "code-editor", value: "c++ · clangd · workspace sync" },
      { key: "ir-viewer", value: "live llvm IR feed" },
      { key: "terminal", value: "bash · clang · opt · llvm-lit" },
      { key: "problem", value: "static content + IR fixtures" },
    ],
    layoutLeft: "code-editor + problem",
    layoutRight: "ir-viewer / terminal",
  },
  status: ["clangd · llvm 17", "c++17"],
};

const EBPF: DomainContent = {
  id: "ebpf",
  label: "eBPF",
  windowTitle: "Senior eBPF Engineer Interview",
  editor: {
    language: "C",
    languages: ["C", "Rust", "Go", "TypeScript"],
    Body: EbpfCodeBody,
  },
  topPanel: {
    name: "Verifier Output",
    badges: ["live verifier feed"],
    Body: VerifierBody,
  },
  terminal: {
    badges: ["shared terminal", "candidate controls"],
    Body: EbpfTerminalBody,
    candidateCmd: "bpftool prog load xdp_drop.bpf.o /sys/fs/bpf/xdp_drop",
  },
  problem:
    "Write an XDP program that drops TCP traffic on port 8080 unless the source IP appears in a runtime-updatable whitelist map. The program must pass the BPF verifier.",
  template: {
    name: "Senior eBPF Engineer Interview",
    domain: "eBPF / Systems Engineering",
    domainSlug: "ebpf-systems",
    components: [
      { key: "code-editor", value: "c · bpf target · workspace sync" },
      { key: "verifier-output", value: "live BPF verifier feed" },
      { key: "terminal", value: "bash · bpftool · ip" },
      { key: "problem", value: "static content + packet generator" },
    ],
    layoutLeft: "code-editor + problem",
    layoutRight: "verifier-output / terminal",
  },
  status: ["linux 6.5 · clang-bpf", "veth · packet gen"],
};

const SRE: DomainContent = {
  id: "sre",
  label: "SRE",
  windowTitle: "SRE Incident Response Interview",
  editor: {
    language: "PromQL",
    languages: ["PromQL", "Bash", "Python", "YAML"],
    Body: SreCodeBody,
  },
  topPanel: {
    name: "Metrics Viewer",
    badges: ["live grafana feed"],
    Body: MetricsBody,
  },
  terminal: {
    badges: ["shared terminal", "candidate controls"],
    Body: SreTerminalBody,
    candidateCmd: "kubectl rollout undo deployment/checkout -n production",
  },
  problem:
    "The /api/checkout endpoint is returning 5xx for 12% of requests. Diagnose the root cause using the available tools and propose a fix.",
  template: {
    name: "SRE Incident Response Interview",
    domain: "SRE / Incident Response",
    domainSlug: "sre-incident",
    components: [
      { key: "code-editor", value: "promql · workspace sync" },
      { key: "metrics-viewer", value: "live grafana + loki" },
      { key: "terminal", value: "bash · kubectl · candidate writes" },
      { key: "problem", value: "broken cluster · live state" },
    ],
    layoutLeft: "code-editor + problem",
    layoutRight: "metrics-viewer / terminal",
  },
  status: ["grafana · loki · prom", "30s window"],
};

const DOMAINS: DomainContent[] = [K8S, LLVM, EBPF, SRE];

/* =========================================================================
   Entry point — animated on all screen sizes, static only for reduced-motion
   ========================================================================= */
export function PortalDemo() {
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  if (reducedMotion) {
    return <PortalDemoStatic />;
  }
  return <PortalDemoAnimated />;
}

/* =========================================================================
   Animated demo
   ========================================================================= */
function PortalDemoAnimated() {
  const [stepIdx, setStepIdx] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);
  const [pinned, setPinned] = useState(false);
  const [paused, setPaused] = useState(false);

  const step = STEPS[stepIdx]?.id ?? "empty";
  const sIdx = stepIndex(step);
  const domain = DOMAINS[activeIdx] ?? K8S;

  useEffect(() => {
    if (paused) return;
    const t = window.setTimeout(() => {
      const next = (stepIdx + 1) % STEPS.length;
      // Wrapped to step 0 → advance domain unless pinned
      if (next === 0 && !pinned) {
        setActiveIdx((i) => (i + 1) % DOMAINS.length);
      }
      setStepIdx(next);
    }, STEPS[stepIdx]?.duration ?? 2000);
    return () => window.clearTimeout(t);
  }, [stepIdx, paused, pinned]);

  const onTabClick = (idx: number) => {
    if (idx === activeIdx) {
      // re-clicking current tab restarts its loop
      setStepIdx(0);
      setPinned(true);
      return;
    }
    setActiveIdx(idx);
    setStepIdx(0);
    setPinned(true);
  };

  const reached = (target: StepId) => sIdx >= stepIndex(target);
  const onlyAt = (target: StepId) => step === target;

  const codeEditorVisible = reached("code") && step !== "summary" && step !== "candidate";
  const codeEditorFull = !reached("problem");
  const clusterVisible = reached("cluster") && step !== "summary" && step !== "candidate";
  const clusterFull = !reached("terminal");
  const terminalVisible = reached("terminal") && step !== "summary" && step !== "candidate";
  const problemVisible = reached("problem") && step !== "summary" && step !== "candidate";
  const candidateVisible = step === "candidate";

  const leftColumn = reached("resize") ? "60%" : "56%";

  const sidebarActive = (() => {
    switch (step) {
      case "code":
        return "Code Editor";
      case "cluster":
        return domain.topPanel.name;
      case "terminal":
        return "Terminal";
      case "problem":
        return "Problem Statement";
      default:
        return null;
    }
  })();

  const Editor = domain.editor.Body;
  const TopPanel = domain.topPanel.Body;
  const TopPanelCandidate = domain.topPanel.CandidateBody ?? domain.topPanel.Body;
  const Terminal = domain.terminal.Body;

  return (
    <figure className={`portal-demo ${paused ? "portal-demo--paused" : ""}`}>
      <DomainTabs domains={DOMAINS} activeIdx={activeIdx} pinned={pinned} onClick={onTabClick} />

      <div
        className="portal-demo__window"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <Chrome title={domain.windowTitle} />

        <div
          className="portal-demo__progress"
          key={`progress-${activeIdx}-${stepIdx === 0 ? "r" : "n"}`}
          style={{ animationDuration: `${TOTAL_DURATION_MS}ms` }}
        />

        <div className="portal-demo__body">
          <ActivityBar />
          <Sidebar activeAdd={sidebarActive} topPanelName={domain.topPanel.name} />

          <div className="portal-demo__canvas" style={{ ["--left-col" as string]: leftColumn }}>
            {!reached("code") && (
              <div className="portal-demo__placeholder">
                Add components to build your interview shell.
              </div>
            )}

            <AnimatePresence mode="popLayout">
              {codeEditorVisible && (
                <motion.div
                  key={`code-${domain.id}`}
                  className={`portal-demo__panel portal-demo__panel--code${codeEditorFull ? " portal-demo__panel--full-col" : ""}`}
                  initial={{ opacity: 0, x: -16, scale: 0.98 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
                >
                  <PanelHeader
                    name={`Code Editor — ${domain.editor.language}`}
                    badges={["language server", "workspace files"]}
                  />
                  <Editor />
                  {onlyAt("code") && (
                    <LanguageBadge
                      active={domain.editor.language}
                      options={domain.editor.languages}
                    />
                  )}
                </motion.div>
              )}

              {clusterVisible && (
                <motion.div
                  key={`top-${domain.id}`}
                  className={`portal-demo__panel portal-demo__panel--cluster${clusterFull ? " portal-demo__panel--full-row" : ""}`}
                  initial={{ opacity: 0, x: 16, scale: 0.98 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
                >
                  <PanelHeader name={domain.topPanel.name} badges={domain.topPanel.badges} />
                  <TopPanel />
                </motion.div>
              )}

              {terminalVisible && (
                <motion.div
                  key={`terminal-${domain.id}`}
                  className="portal-demo__panel portal-demo__panel--terminal"
                  initial={{ opacity: 0, y: 16, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
                >
                  <PanelHeader name="Terminal" badges={domain.terminal.badges} />
                  <Terminal />
                </motion.div>
              )}

              {problemVisible && (
                <motion.div
                  key={`problem-${domain.id}`}
                  className="portal-demo__panel portal-demo__panel--problem"
                  initial={{ opacity: 0, y: 16, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
                >
                  <PanelHeader
                    name="Problem Statement"
                    badges={["visible to candidate throughout"]}
                  />
                  <ProblemBody text={domain.problem} typing={onlyAt("problem")} />
                </motion.div>
              )}

              {candidateVisible && (
                <motion.div
                  key={`candidate-${domain.id}`}
                  className="portal-demo__candidate-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <CandidateView domain={domain} Editor={Editor} TopPanel={TopPanelCandidate} />
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {onlyAt("resize") && (
                <motion.div
                  className="portal-demo__resize-cursor"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ left: leftColumn }}
                >
                  <div className="portal-demo__resize-grip" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <StatusBar step={step} domain={domain} />

        <AnimatePresence>
          {(step === "save" || step === "summary") && (
            <motion.div
              key="modal-shell"
              className="portal-demo__modal-shell"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <motion.div
                className="portal-demo__modal"
                initial={{ y: 32, opacity: 0, scale: 0.96 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: 16, opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
              >
                <AnimatePresence mode="wait">
                  {step === "save" ? (
                    <motion.div
                      key={`save-${domain.id}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <SaveModalContent domain={domain} />
                    </motion.div>
                  ) : (
                    <motion.div
                      key={`summary-${domain.id}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <TemplateSummaryContent domain={domain} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </figure>
  );
}

/* =========================================================================
   Tabs
   ========================================================================= */
function DomainTabs({
  domains,
  activeIdx,
  pinned,
  onClick,
}: {
  domains: DomainContent[];
  activeIdx: number;
  pinned: boolean;
  onClick: (idx: number) => void;
}) {
  return (
    <div className="portal-tabs" role="tablist" aria-label="Interview domain">
      {domains.map((d, i) => {
        const active = i === activeIdx;
        return (
          <button
            key={d.id}
            type="button"
            role="tab"
            aria-selected={active}
            className={`portal-tabs__tab${active ? " portal-tabs__tab--active" : ""}`}
            onClick={() => onClick(i)}
          >
            <span className="portal-tabs__label">{d.label}</span>
            {active && (
              <motion.span
                layoutId="portal-tab-underline"
                className="portal-tabs__underline"
                transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
              />
            )}
          </button>
        );
      })}
      <span className="portal-tabs__hint">
        {pinned ? "pinned" : "auto-rotating · click to pin"}
      </span>
    </div>
  );
}

/* =========================================================================
   Window chrome / activity bar / sidebar / status bar
   ========================================================================= */
function Chrome({ title }: { title: string }) {
  return (
    <div className="portal-demo__chrome">
      <div className="portal-demo__lights" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <div className="portal-demo__chrome-title">{copy.brand} Builder · {title}</div>
      <div className="portal-demo__chrome-spacer" />
    </div>
  );
}

function ActivityBar() {
  return (
    <div className="portal-demo__activity" aria-hidden="true">
      <span className="portal-demo__activity-icon portal-demo__activity-icon--active">▦</span>
      <span className="portal-demo__activity-icon">⌕</span>
      <span className="portal-demo__activity-icon">⎇</span>
      <span className="portal-demo__activity-icon">⊞</span>
      <span className="portal-demo__activity-icon">◷</span>
    </div>
  );
}

function Sidebar({
  activeAdd,
  topPanelName,
}: {
  activeAdd: string | null;
  topPanelName: string;
}) {
  const groups = [
    { heading: "Interaction", items: ["Code Editor", "Terminal", "Notebook"] },
    {
      heading: "Viewers",
      items: [topPanelName, "IR Viewer", "Web Preview", "Hardware Viewer"].filter(
        (item, i, arr) => arr.indexOf(item) === i
      ),
    },
    { heading: "Content", items: ["Problem Statement"] },
  ];
  return (
    <aside className="portal-demo__sidebar">
      <div className="portal-demo__sidebar-head">Interview Components</div>
      {groups.map((g) => (
        <div key={g.heading} className="portal-demo__sidebar-group">
          <div className="portal-demo__sidebar-heading">{g.heading}</div>
          {g.items.map((item) => (
            <div
              key={item}
              className={`portal-demo__sidebar-item${activeAdd === item ? " portal-demo__sidebar-item--active" : ""}`}
            >
              <span>{item}</span>
              <span
                className={`portal-demo__sidebar-add${activeAdd === item ? " portal-demo__sidebar-add--pulse" : ""}`}
              >
                + Add
              </span>
            </div>
          ))}
        </div>
      ))}
    </aside>
  );
}

function StatusBar({ step, domain }: { step: StepId; domain: DomainContent }) {
  const showSave = stepIndex(step) >= stepIndex("problem") && step !== "summary";
  const pulsing = step === "resize" || step === "save";
  return (
    <footer className="portal-demo__status">
      <span className="portal-demo__status-item">
        <span className="portal-demo__status-dot" /> connected
      </span>
      {domain.status.map((s) => (
        <span key={s} className="portal-demo__status-item">
          {s}
        </span>
      ))}
      <span className="portal-demo__status-spacer" />
      {showSave ? (
        <span className={`portal-demo__save${pulsing ? " portal-demo__save--pulse" : ""}`}>
          <span aria-hidden="true">⊙</span>
          Save as Interview Template
        </span>
      ) : (
        <span className="portal-demo__status-item">9 components available</span>
      )}
    </footer>
  );
}

/* =========================================================================
   Save modal + template summary + candidate view
   ========================================================================= */
function SaveModalContent({ domain }: { domain: DomainContent }) {
  return (
    <div className="portal-demo__save-modal">
      <h4 className="portal-demo__save-title">Save as Interview Template</h4>
      <div className="portal-demo__save-row">
        <span className="portal-demo__save-key">Name</span>
        <span className="portal-demo__save-val">{domain.template.name}</span>
      </div>
      <div className="portal-demo__save-row">
        <span className="portal-demo__save-key">Domain</span>
        <span className="portal-demo__save-val">{domain.template.domain}</span>
      </div>
      <div className="portal-demo__save-divider" />
      <div className="portal-demo__save-section">Components captured</div>
      <ul className="portal-demo__save-list">
        {domain.template.components.map((c) => (
          <li key={c.key}>
            <span className="portal-demo__save-check">✓</span>
            <span className="portal-demo__save-component">{c.key}</span>
            <span className="portal-demo__save-meta">{c.value}</span>
          </li>
        ))}
      </ul>
      <div className="portal-demo__save-actions">
        <span className="portal-demo__save-cta portal-demo__save-cta--pulse">Save Template</span>
      </div>
    </div>
  );
}

function TemplateSummaryContent({ domain }: { domain: DomainContent }) {
  return (
    <div className="portal-demo__summary">
      <div className="portal-demo__summary-success">
        <span className="portal-demo__summary-check">✓</span>
        <span>Template saved.</span>
      </div>
      <pre className="portal-demo__summary-code">
        <span>
          <span className="pk">template:</span> <span className="pv">{domain.template.name}</span>
        </span>
        <span>
          <span className="pk">domain:</span>{" "}
          <span className="pv">{domain.template.domainSlug}</span>
        </span>
        <span />
        <span>
          <span className="pk">components:</span>
        </span>
        {domain.template.components.map((c) => (
          <span key={c.key}>
            {"  "}
            <span className="pk">{c.key.padEnd(16)}</span> <span className="pv">{c.value}</span>
          </span>
        ))}
        <span />
        <span>
          <span className="pk">workspace:</span>
        </span>
        <span>
          {"  "}
          <span className="pk">/workspace</span>{" "}
          <span className="pv">synced · both participants see changes live</span>
        </span>
        <span />
        <span>
          <span className="pk">layout:</span>
        </span>
        <span>
          {"  "}
          <span className="pk">left 60%</span> →{" "}
          <span className="pv">{domain.template.layoutLeft}</span>
        </span>
        <span>
          {"  "}
          <span className="pk">right 40%</span> →{" "}
          <span className="pv">{domain.template.layoutRight}</span>
        </span>
      </pre>
      <p className="portal-demo__summary-caption">
        This template recreates the exact same environment for every candidate.
      </p>
    </div>
  );
}

function CandidateView({
  domain,
  Editor,
  TopPanel,
}: {
  domain: DomainContent;
  Editor: React.ComponentType;
  TopPanel: React.ComponentType;
}) {
  const { display, done } = useTypewriter(domain.terminal.candidateCmd, {
    speed: 60,
    startDelay: 700,
  });
  return (
    <div className="portal-demo__candidate">
      <div className="portal-demo__candidate-label portal-demo__candidate-label--top">
        What the candidate sees when they join →
      </div>

      <div className="portal-demo__candidate-grid">
        <div className="portal-demo__panel portal-demo__panel--code">
          <PanelHeader
            name={`Code Editor — ${domain.editor.language}`}
            badges={["language server", "workspace files"]}
          />
          <Editor />
        </div>

        <div className="portal-demo__panel portal-demo__panel--cluster">
          <PanelHeader name={domain.topPanel.name} badges={domain.topPanel.badges} />
          <TopPanel />
        </div>

        <div className="portal-demo__panel portal-demo__panel--problem">
          <PanelHeader name="Problem Statement" badges={["visible to candidate throughout"]} />
          <div className="portal-demo__problem">
            <p>{domain.problem}</p>
          </div>
        </div>

        <div className="portal-demo__panel portal-demo__panel--terminal">
          <PanelHeader name="Terminal" badges={domain.terminal.badges} />
          <div className="portal-demo__term">
            <div className="portal-demo__term-line">
              <span className="prompt">$</span> {display}
              {!done && <span className="portal-demo__caret" />}
            </div>
          </div>
        </div>
      </div>

      <div className="portal-demo__candidate-label portal-demo__candidate-label--bottom">
        No setup. No instructions. The environment is already running.
      </div>
    </div>
  );
}

/* =========================================================================
   Static (mobile / reduced-motion) fallback — full tabs, no animations
   ========================================================================= */
function PortalDemoStatic() {
  const [activeIdx, setActiveIdx] = useState(0);
  const domain = DOMAINS[activeIdx] ?? K8S;

  return (
    <figure className="portal-demo portal-demo--static">
      <div className="portal-tabs" role="tablist" aria-label="Interview domain">
        {DOMAINS.map((d, i) => {
          const active = i === activeIdx;
          return (
            <button
              key={d.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`portal-tabs__tab${active ? " portal-tabs__tab--active" : ""}`}
              onClick={() => setActiveIdx(i)}
            >
              <span className="portal-tabs__label">{d.label}</span>
              {active && <span className="portal-tabs__underline" />}
            </button>
          );
        })}
      </div>

      <div className="portal-demo__static-card">
        <TemplateSummaryContent domain={domain} />
      </div>
    </figure>
  );
}

export const PORTAL_DEMO_DURATION_MS = TOTAL_DURATION_MS;
